import { useEffect, useState } from 'react';
import { useCache } from '@elfsight/widget-utils';
import { useSettings } from '@wu/widget-core';
import { DEFAULT_API_UNITS } from '../../../constants';
import type { RequestResponse } from './request';
import { request } from './request';

type DataProps = {
  children: Function;
  sourceName: string | string[];
  useUserLocation: boolean;
  endpoint: string;
  sourceType: string;
  params: object;
};

export type ChildrenData<T> = {
  data: RequestResponse<T> | null;
  loading: boolean;
  error: { code: number; message: string } | null;
};

export function Data<T>({
  children,
  sourceName,
  useUserLocation,
  endpoint,
  sourceType,
  params = {}
}: DataProps) {
  const { widgetToken } = useSettings();

  const [data, setData] = useState<RequestResponse<T> | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<unknown | null>(null);

  const { getCache, setCache } = useCache();

  useEffect(() => {
    fetchData();
  }, [sourceName, useUserLocation, params]);

  const fetchData = async () => {
    setLoading(true);

    try {
      await loadData();
    } catch (error) {
      setError(error);
    }

    setLoading(false);
  };

  const checkCache = (url: string) => {
    return new Promise<RequestResponse<T> | null>((resolve) => {
      getCache(url, 300)
        .then((response) => resolve(response as RequestResponse<T>))
        .catch(() => resolve(null));
    });
  };

  const loadData = () => {
    const apiParams: Record<string, string> = {
      ...params,
      units: DEFAULT_API_UNITS,
      endpoint,
      useUserLocation: String(useUserLocation)
    };

    const cacheKey = `${endpoint}/${sourceName}/${DEFAULT_API_UNITS}/${Object.entries(
      apiParams
    )
      .map(([key, val]) => `${key}=${val}`)
      .join('&')}`;

    return new Promise((resolve, reject) => {
      checkCache(cacheKey).then(async (cacheData) => {
        if (cacheData) {
          setData(cacheData);
          resolve(true);

          return;
        }

        const response = await request<T>({
          widgetToken,
          params: apiParams,
          endpoint: `${sourceName}${sourceType ? `/${sourceType}` : ''}`
        });

        if ('error' in response) {
          setData(null);
          reject(response.error);
          return;
        }

        setCache(cacheKey, response);
        setData(response);

        resolve(true);
      });
    });
  };

  return children({
    data,
    error,
    loading
  });
}
